import { useQuery } from '@tanstack/react-query';
import {
  getBillingLicensePlansQuery,
  getBusinessLicenseQuery,
  getScheduledBusinessLicensesQuery,
} from 'query/billing';
import { PageSizing } from 'components/Layout/PageContentWidth';
import { ContentCard, PageWrapper } from '../styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BillingChangeToPlan,
  BillingChangeToPlanPayload,
  BillingLicensePlans,
} from '@kizen/api/billing';
import { PlanRow } from './components/PlanRow';
import { Spacer } from '@kizen/kds/Spacer';
import { Button } from '@kizen/kds/Button';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { Estimator } from './components/Estimator';
import Loader from '__components/Kizen/Loader';
import { Comparison } from './components/Comparison';
import {
  CurrentlyActivePlan,
  useSelectedPlanResolution,
} from './hooks/useSelectedPlanResolution';
import { arePlansEqual } from './util';
import { deferExecution } from 'utility/defer';
import BillingService from 'services/BillingService';
import useModal from '__components/Modals/useModal';
import { useToast } from '__components/ToastProvider';
import { toastVariant } from '__components/ToastProvider';
import ConfirmNavigationModal from '__components/Modals/presets/ConfirmNavigation';
import { UserCountErrorDialog } from './components/Dialogs/UserCountErrorDialog';
import { UnsavedChangesDialog } from './components/Dialogs/UnsavedChangesDialog';
import { PendingPlanChangesDialog } from './components/Dialogs/PendingPlanChanges';
import { DowngradeCancellationDialog } from './components/Dialogs/DowngradeCancellationDialog';
import { ConfirmPlanChangesDialog } from './components/Dialogs/ConfirmPlanChangesDialog';
import { PlanSummary } from './components/PlanSummary';
import { MissingBillingDetailsDialog } from './components/Dialogs/MissingBillingDetails';
import { useHistory } from 'react-router-dom';
import { RequiresPaidPlanToCancelDowngrade } from './components/Dialogs/RequiresPaidPlanToCancelDowngrade';

export const PlanPage = () => {
  const { t } = useTranslation();
  const [showComparisonTable, setShowComparisonTable] = useState(false);
  const generalPlanRowRef = useRef<HTMLDivElement>(null);
  const [billingPreviewResponse, setBillingPreviewResponse] =
    useState<BillingChangeToPlan | null>(null);
  const [pendingRecalculation, setPendingRecalculation] = useState(false);
  const history = useHistory();

  const [showToast] = useToast();

  // License templates are available plans to choose from
  const { data: licenseTemplates, isLoading: licenseTemplatesLoading } =
    useQuery(getBillingLicensePlansQuery());

  // License plans are the plans that the business has active before making any changes
  const {
    data: licensePlans,
    isLoading: licensePlansLoading,
    refetch: refetchLicensePlans,
  } = useQuery(getBusinessLicenseQuery());

  // These are downgrades that are pending at a later date
  const {
    data: pendingChanges,
    isLoading: pendingChangesLoading,
    refetch: refetchPendingChanges,
  } = useQuery(getScheduledBusinessLicensesQuery());

  /*
    These indices will be null if we should fall back on the existing active plan (i.e. no changes needed).
    If the index is -1, it means the user selected a plan, then reselected the legacy plan they were on.
    In this case, it should be treated the same as if the user never selected a new plan.
    Otherwise, the value will be between 0 and n, where n is the index of the last available plan in the list
    for the particular category.
  */
  const [currentSelectedGeneralPlanIndex, setCurrentSelectedGeneralPlanIndex] =
    useState<number | null>(null);
  const [
    currentSelectedAutomationPlanIndex,
    setCurrentSelectedAutomationPlanIndex,
  ] = useState<number | null>(null);
  const [
    currentSelectedMarketingPlanIndex,
    setCurrentSelectedMarketingPlanIndex,
  ] = useState<number | null>(null);

  /*
    The manually entered user count. Should be null when the user hasn't made an explicit selection
  */
  const [chosenUserCount, setChosenUserCount] = useState<number | null>(null);

  const resetSelections = useCallback(() => {
    setCurrentSelectedGeneralPlanIndex(null);
    setCurrentSelectedAutomationPlanIndex(null);
    setCurrentSelectedMarketingPlanIndex(null);
    setChosenUserCount(null);
  }, []);

  /* 
    These are the current plans for the business. They don't have to be published, so might not appear
    in the available templates. They should be selectable until the user changes to a published plan,
    at which point there is no longer an active legacy plan.
  */
  const { currentGeneralPlan, currentAutomationPlan, currentMarketingPlan } =
    useMemo(() => {
      let general: CurrentlyActivePlan;
      let automation: CurrentlyActivePlan;
      let marketing: CurrentlyActivePlan;

      licensePlans?.forEach((plan) => {
        if (plan.license_plan.plan_category === 'general') {
          general = plan;
        } else if (plan.license_plan.plan_category === 'automation') {
          automation = plan;
        } else if (plan.license_plan.plan_category === 'email') {
          marketing = plan;
        }
      });

      return {
        currentGeneralPlan: general,
        currentAutomationPlan: automation,
        currentMarketingPlan: marketing,
      };
    }, [licensePlans]);

  const hasFreeAutomationPlan = Boolean(
    currentAutomationPlan?.price.amount === 0
  );
  const hasFreeMarketingPlan = Boolean(
    currentMarketingPlan?.price.amount === 0
  );

  /*
    These are the plans that are pending downgrade for the business
  */
  const { pendingGeneralPlan, pendingAutomationPlan, pendingMarketingPlan } =
    useMemo(() => {
      let general: CurrentlyActivePlan;
      let automation: CurrentlyActivePlan;
      let marketing: CurrentlyActivePlan;

      pendingChanges?.forEach((plan) => {
        if (plan.license_plan.plan_category === 'general') {
          general = plan;
        } else if (plan.license_plan.plan_category === 'automation') {
          automation = plan;
        } else if (plan.license_plan.plan_category === 'email') {
          marketing = plan;
        }
      });

      return {
        pendingGeneralPlan: general,
        pendingAutomationPlan: automation,
        pendingMarketingPlan: marketing,
      };
    }, [pendingChanges]);

  const hasFreePendingAutomationPlan = Boolean(
    pendingAutomationPlan?.price.amount === 0
  );

  const hasFreePendingMarketingPlan = Boolean(
    pendingMarketingPlan?.price.amount === 0
  );

  const hasAnyPendingPlans = Boolean(
    pendingGeneralPlan || pendingAutomationPlan || pendingMarketingPlan
  );

  // These are the plans that are currently published and selectable as a plan change
  const { corePlatformPlans, automationsPlans, marketingPlans } =
    useMemo(() => {
      const core: BillingLicensePlans['license_templates'] = [];
      const automations: BillingLicensePlans['license_templates'] = [];
      const marketing: BillingLicensePlans['license_templates'] = [];

      licenseTemplates?.license_templates?.forEach((plan) => {
        if (plan.plan_category === 'general') {
          core.push(plan);
        } else if (plan.plan_category === 'automation') {
          automations.push(plan);
        } else if (plan.plan_category === 'email') {
          marketing.push(plan);
        }
      });

      return {
        corePlatformPlans: core.sort((a, b) => a.tier_order - b.tier_order),
        automationsPlans: automations.sort(
          (a, b) => a.tier_order - b.tier_order
        ),
        marketingPlans: marketing.sort((a, b) => a.tier_order - b.tier_order),
      };
    }, [licenseTemplates]);

  const isLoading =
    licenseTemplatesLoading || licensePlansLoading || pendingChangesLoading;

  // Currently chosen core platform (general) plan in the UI. Could be one of the published plans,
  // or the active legacy plan.
  const { current: currentSelectedCorePlatformPlan, isPaid: isCorePlanPaid } =
    useSelectedPlanResolution({
      plans: corePlatformPlans,
      currentPlan: currentGeneralPlan,
      currentSelectedPlanIndex: currentSelectedGeneralPlanIndex,
    });

  // Currently chosen automation plan in the UI. Could be one of the published plans,
  // or the active legacy plan.
  const {
    current: currentSelectedAutomationPlan,
    next: nextSelectedAutomationPlan,
    showEstimator: showAutomationEstimator,
  } = useSelectedPlanResolution({
    plans: automationsPlans,
    currentPlan: currentAutomationPlan,
    currentSelectedPlanIndex: currentSelectedAutomationPlanIndex,
  });

  const hasFreeSelectedAutomationPlan = Boolean(
    currentSelectedAutomationPlan?.price?.amount === 0
  );

  // Currently chosen marketing (emails) plan in the UI. Could be one of the published plans,
  // or the active legacy plan.
  const {
    current: currentSelectedMarketingPlan,
    next: nextSelectedMarketingPlan,
    showEstimator: showMarketingEstimator,
  } = useSelectedPlanResolution({
    plans: marketingPlans,
    currentPlan: currentMarketingPlan,
    currentSelectedPlanIndex: currentSelectedMarketingPlanIndex,
  });

  const hasFreeSelectedMarketingPlan = Boolean(
    currentSelectedMarketingPlan?.price?.amount === 0
  );

  // If the user has made a selection, use that, otherwise pull the max user count from the plan,
  // which for free plans will be fixed, and for paid plans will be whatever they have previously
  // chosen.
  const numUsersForCalculation =
    chosenUserCount ?? (currentGeneralPlan?.config?.limits as any)?.max_users;

  const scrollToGeneralPlanRow = useCallback(() => {
    deferExecution(() => {
      generalPlanRowRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    });
  }, []);

  const isCorePlanDirty = useMemo(() => {
    return !arePlansEqual(currentSelectedCorePlatformPlan, currentGeneralPlan);
  }, [currentGeneralPlan, currentSelectedCorePlatformPlan]);

  const isAutomationPlanDirty = useMemo(() => {
    return !arePlansEqual(currentSelectedAutomationPlan, currentAutomationPlan);
  }, [currentAutomationPlan, currentSelectedAutomationPlan]);

  const isMarketingPlanDirty = useMemo(() => {
    return !arePlansEqual(currentSelectedMarketingPlan, currentMarketingPlan);
  }, [currentMarketingPlan, currentSelectedMarketingPlan]);

  const isUserCountDirty =
    numUsersForCalculation !==
    (currentGeneralPlan?.config?.limits as any)?.max_users;

  const isDirty =
    isCorePlanDirty ||
    isAutomationPlanDirty ||
    isMarketingPlanDirty ||
    isUserCountDirty;

  const [resetSelectionsModalProps, , { show: showResetSelectionsModal }] =
    useModal({
      handleSubmit: resetSelections,
    });

  const [
    lockedPlanCategoryModalProps,
    ,
    { show: showLockedPlanCategoryModal },
  ] = useModal();

  const [invalidUserCountModalProps, , { show: showInvalidUserCountModal }] =
    useModal();
  const [missingBillingInfoModalProps, , { show: showMissingInfoModal }] =
    useModal({
      handleSubmit: () => history.push('/settings/billing/details'),
    });

  const handleMaxUsersError = useCallback(() => {
    showInvalidUserCountModal();
    setChosenUserCount(null);
  }, [showInvalidUserCountModal]);

  const handleMissingBillingDetailsError = useCallback(() => {
    resetSelections();
    showMissingInfoModal();
  }, [showMissingInfoModal, resetSelections]);

  const handleTemplatePlanError = useCallback(
    (
      planTypes: Array<{
        isAutomation: boolean;
        isCore: boolean;
        isMarketing: boolean;
      }>
    ) => {
      if (planTypes.some((p) => p.isAutomation)) {
        setCurrentSelectedAutomationPlanIndex(null);
      }
      if (planTypes.some((p) => p.isCore)) {
        setChosenUserCount(null);
        setCurrentSelectedGeneralPlanIndex(null);
      }
      if (planTypes.some((p) => p.isMarketing)) {
        setCurrentSelectedMarketingPlanIndex(null);
      }

      showLockedPlanCategoryModal();
    },
    [showLockedPlanCategoryModal]
  );

  const refreshBillingEstimate = useCallback(
    async (
      templates: Array<string> = [],
      users?: number | null,
      planIds?: { core?: string; automations?: string; marketing?: string }
    ) => {
      const payload: BillingChangeToPlanPayload = {};

      let hasTemplates = false;
      let hasUsers = false;

      if (templates.length > 0) {
        hasTemplates = true;
        payload.template_plan_ids = templates;
      }

      if (typeof users === 'number') {
        hasUsers = true;
        payload.max_users = users;
      }

      if (!hasTemplates && !hasUsers) {
        setBillingPreviewResponse(null);
        setPendingRecalculation(false);
        return;
      }

      setPendingRecalculation(true);

      try {
        const estimate = await BillingService.changeToPlan(payload, true);
        setBillingPreviewResponse(estimate);
      } catch (ex: any) {
        const planTypes = templates.map((id) => {
          const isAutomation = id === planIds?.automations;
          const isMarketing = id === planIds?.marketing;
          const isCore = id === planIds?.core;

          return {
            isAutomation,
            isMarketing,
            isCore,
          };
        });

        const errors = ex.response?.data?.errors as any;

        if (errors?.max_users) {
          handleMaxUsersError();
        } else if (errors?.template_plan_ids) {
          handleTemplatePlanError(planTypes);
        } else if (
          errors?.non_field_errors &&
          errors.non_field_errors.code ===
            'default_payment_method_not_set_on_business'
        ) {
          handleMissingBillingDetailsError();
        } else {
          showToast({
            message:
              ex.response?.data?.message ||
              t(
                'An unexpected error occurred. Please make your selections and try again.'
              ),
            variant: toastVariant.FAILURE,
          });
          resetSelections();
        }
      } finally {
        setPendingRecalculation(false);
      }
    },
    [
      resetSelections,
      handleMaxUsersError,
      handleTemplatePlanError,
      showToast,
      t,
      handleMissingBillingDetailsError,
    ]
  );

  const [
    cannotCancelDowngradeModalProps,
    ,
    { show: showCannotCancelDowngradeModal },
  ] = useModal();

  const handleCancelPendingDowngrade = useCallback(
    async (ids: Array<string>) => {
      try {
        await BillingService.cancelPendingChange(ids);
        refetchPendingChanges();
        refetchLicensePlans();
      } catch (ex: any) {
        const errors = ex.response?.data?.errors as any;

        if (
          errors?.business_license_ids?.code ===
          'cannot_cancel_scheduled_downgrade_plans_without_also_cancelling_core_platform_downgrade_validation_error'
        ) {
          showCannotCancelDowngradeModal();
        } else {
          showToast({
            message:
              ex.response?.data?.message ||
              t(
                'An unexpected error occurred. Please try your downgrade again.'
              ),
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [
      refetchPendingChanges,
      refetchLicensePlans,
      showToast,
      t,
      showCannotCancelDowngradeModal,
    ]
  );

  const commitPlanChanges = useCallback(async () => {
    const templates = [
      isAutomationPlanDirty
        ? currentSelectedAutomationPlan?.template_id ?? ''
        : '',
      isCorePlanDirty ? currentSelectedCorePlatformPlan?.template_id ?? '' : '',
      isMarketingPlanDirty
        ? currentSelectedMarketingPlan?.template_id ?? ''
        : '',
    ].filter(Boolean);

    const users = chosenUserCount;

    const payload: BillingChangeToPlanPayload = {};

    if (templates.length > 0) {
      payload.template_plan_ids = templates;
    }

    if (typeof users === 'number') {
      payload.max_users = users;
    }

    await BillingService.changeToPlan(payload, false);

    // There is a race condition on the backend where changing a plan doesn't immediately cause the new plan
    // to be returned. We need to wait a bit before refetching. This should be a temporary
    // solution but it's what is recommended by the backend team for now.
    await new Promise((resolve) => setTimeout(resolve, 1000));

    resetSelections();
    refetchLicensePlans();
    refetchPendingChanges();
  }, [
    currentSelectedAutomationPlan,
    currentSelectedCorePlatformPlan,
    currentSelectedMarketingPlan,
    isAutomationPlanDirty,
    isCorePlanDirty,
    isMarketingPlanDirty,
    chosenUserCount,
    refetchLicensePlans,
    refetchPendingChanges,
    resetSelections,
  ]);

  const refreshBillingEstimateForCurrentState = useCallback(() => {
    return refreshBillingEstimate(
      [
        isAutomationPlanDirty
          ? currentSelectedAutomationPlan?.template_id ?? ''
          : '',
        isCorePlanDirty
          ? currentSelectedCorePlatformPlan?.template_id ?? ''
          : '',
        isMarketingPlanDirty
          ? currentSelectedMarketingPlan?.template_id ?? ''
          : '',
      ].filter(Boolean),
      isUserCountDirty ? chosenUserCount : undefined,
      {
        core: currentSelectedCorePlatformPlan?.template_id,
        automations: currentSelectedAutomationPlan?.template_id,
        marketing: currentSelectedMarketingPlan?.template_id,
      }
    );
  }, [
    currentSelectedAutomationPlan,
    currentSelectedCorePlatformPlan,
    currentSelectedMarketingPlan,
    refreshBillingEstimate,
    isAutomationPlanDirty,
    isCorePlanDirty,
    isMarketingPlanDirty,
    chosenUserCount,
    isUserCountDirty,
  ]);

  const [confirmSaveChangesModalProps, , { show: showConfirmSaveChanges }] =
    useModal({
      handleSubmit: commitPlanChanges,
    });

  const [
    pendingPlanDowngradeCancellations,
    setPendingPlanDowngradeCancellations,
  ] = useState<Array<string>>([]);

  const [cancelDowngradeModalProps, , { show: showConfirmCancelDowngrade }] =
    useModal({
      handleSubmit: () => {
        handleCancelPendingDowngrade(pendingPlanDowngradeCancellations);
        setPendingPlanDowngradeCancellations([]);
      },
    });

  const clickSaveChanges = useCallback(() => {
    showConfirmSaveChanges();
    refreshBillingEstimateForCurrentState();
  }, [showConfirmSaveChanges, refreshBillingEstimateForCurrentState]);

  useEffect(() => {
    refreshBillingEstimateForCurrentState();
  }, [refreshBillingEstimateForCurrentState]);

  const currentMonthlyTotal = useMemo(() => {
    const automationPlanCost = currentAutomationPlan?.price?.amount ?? 0;
    const marketingPlanCost = currentMarketingPlan?.price?.amount ?? 0;
    const corePlatformPlanCostPerSeat = currentGeneralPlan?.price?.amount ?? 0;
    const numSeats =
      (currentGeneralPlan?.config?.limits as any)?.max_users ?? 0;

    return (
      automationPlanCost +
      marketingPlanCost +
      corePlatformPlanCostPerSeat * numSeats
    );
  }, [currentAutomationPlan, currentGeneralPlan, currentMarketingPlan]);

  const willAutomationPlanBeFree =
    hasFreeAutomationPlan ||
    hasFreePendingAutomationPlan ||
    hasFreeSelectedAutomationPlan;

  const willMarketingPlanBeFree =
    hasFreeMarketingPlan ||
    hasFreePendingMarketingPlan ||
    hasFreeSelectedMarketingPlan;

  const canChooseFreeCorePlan =
    willAutomationPlanBeFree && willMarketingPlanBeFree;

  if (isLoading) {
    return <Loader loading />;
  }

  return (
    <PageSizing>
      <PageWrapper>
        <div className="grid grid-cols-[2.5fr_1fr] gap-20 pb-20">
          <div>
            <ContentCard>
              <div>
                <PlanRow
                  ref={generalPlanRowRef}
                  plans={corePlatformPlans}
                  title={t('Kizen Core Platform')}
                  period="mo"
                  unit="usr"
                  currentPlan={currentGeneralPlan}
                  selectedPlanIndex={currentSelectedGeneralPlanIndex}
                  setSelectedPlanIndex={setCurrentSelectedGeneralPlanIndex}
                  numberInput
                  numberInputLabel={t('Total Platform Users')}
                  numberInputValue={numUsersForCalculation}
                  onNumberInputValueChange={setChosenUserCount}
                  pendingDowngradePlan={pendingGeneralPlan}
                  onCancelDowngrade={(id) => {
                    setPendingPlanDowngradeCancellations([id]);
                    showConfirmCancelDowngrade();
                  }}
                  downgradeDate={pendingGeneralPlan?.start_date}
                  freePlanSelectable={canChooseFreeCorePlan}
                />
                <Spacer size={20} />
                <div className="flex justify-center">
                  <Button
                    variant="text"
                    color="secondary"
                    rightIcon={
                      showComparisonTable
                        ? 'custom-chevron-up'
                        : 'custom-chevron-down'
                    }
                    maxWidth={false}
                    size="small"
                    onClick={() => {
                      setShowComparisonTable((prev) => !prev);
                    }}
                  >
                    {t('See Complete Features Comparison')}
                  </Button>
                </div>
                {showComparisonTable ? (
                  <Comparison
                    plans={corePlatformPlans}
                    labels={licenseTemplates?.feature_labels ?? {}}
                    labelKey="general"
                  />
                ) : null}
                <Spacer size={30} />
                <hr className="my-0 mx-24" />
                <Spacer size={30} />
                <PlanRow
                  plans={automationsPlans}
                  title="Automations"
                  period="mo"
                  currentPlan={currentAutomationPlan}
                  selectedPlanIndex={currentSelectedAutomationPlanIndex}
                  setSelectedPlanIndex={setCurrentSelectedAutomationPlanIndex}
                  selectable={isCorePlanPaid}
                  scrollToGeneralPlanRow={scrollToGeneralPlanRow}
                  pendingDowngradePlan={pendingAutomationPlan}
                  onCancelDowngrade={(id) => {
                    setPendingPlanDowngradeCancellations([id]);
                    showConfirmCancelDowngrade();
                  }}
                  downgradeDate={pendingAutomationPlan?.start_date}
                />
                {showAutomationEstimator ? (
                  <>
                    <Spacer size={20} />
                    <Estimator
                      period={t('Monthly')}
                      periodAbbreviated={t('mo')}
                      entityPlural={t('Tasks')}
                      start={
                        currentSelectedAutomationPlan?.limits
                          .monthly_tasks_limit
                      }
                      /*
                        When the next plan is not self-serve, the max will be 1,000,000. For now,
                        this is not backend-driven.
                      */
                      end={
                        nextSelectedAutomationPlan?.limits
                          .monthly_tasks_limit ?? 2_000_000
                      }
                      unitSize={
                        currentSelectedAutomationPlan?.overage.unit_size
                      }
                      unitPrice={
                        currentSelectedAutomationPlan?.overage.price_per_unit
                      }
                      cta={
                        nextSelectedAutomationPlan ? (
                          <Typography weight="semibold">
                            {t('Upgrade to {{planName}}', {
                              planName: nextSelectedAutomationPlan.name,
                            })}
                          </Typography>
                        ) : (
                          <Typography
                            variant="link"
                            href="https://kizen.com/contact-sales"
                            target="_blank"
                            weight="semibold"
                          >
                            {t('Contact Sales')}
                          </Typography>
                        )
                      }
                      isMax={!nextSelectedAutomationPlan}
                    />
                  </>
                ) : null}

                <Spacer size={30} />
                <hr className="my-0 mx-24" />
                <Spacer size={30} />
                <PlanRow
                  plans={marketingPlans}
                  title="Marketing"
                  period="mo"
                  currentPlan={currentMarketingPlan}
                  selectedPlanIndex={currentSelectedMarketingPlanIndex}
                  setSelectedPlanIndex={setCurrentSelectedMarketingPlanIndex}
                  selectable={isCorePlanPaid}
                  scrollToGeneralPlanRow={scrollToGeneralPlanRow}
                  pendingDowngradePlan={pendingMarketingPlan}
                  onCancelDowngrade={(id) => {
                    setPendingPlanDowngradeCancellations([id]);
                    showConfirmCancelDowngrade();
                  }}
                  downgradeDate={pendingMarketingPlan?.start_date}
                />
                {showMarketingEstimator ? (
                  <>
                    <Spacer size={20} />
                    <Estimator
                      period={t('Monthly')}
                      periodAbbreviated={t('mo')}
                      entityPlural={t('Emails')}
                      start={
                        currentSelectedMarketingPlan?.limits
                          .monthly_emails_limit
                      }
                      /*
                        When the next plan is not self-serve, the max will be 2,000,000. For now,
                        this is not backend-driven.
                      */
                      end={
                        nextSelectedMarketingPlan?.limits
                          .monthly_emails_limit ?? 1_000_000
                      }
                      unitSize={currentSelectedMarketingPlan?.overage.unit_size}
                      unitPrice={
                        currentSelectedMarketingPlan?.overage.price_per_unit
                      }
                      cta={
                        nextSelectedMarketingPlan ? (
                          <Typography weight="semibold">
                            {t('Upgrade to {{planName}}', {
                              planName: nextSelectedMarketingPlan.name,
                            })}
                          </Typography>
                        ) : (
                          <Typography
                            variant="link"
                            href="https://kizen.com/contact-sales"
                            target="_blank"
                            weight="semibold"
                          >
                            {t('Contact Sales')}
                          </Typography>
                        )
                      }
                      isMax={!nextSelectedMarketingPlan}
                    />
                  </>
                ) : null}
              </div>
            </ContentCard>
          </div>
          <div>
            <div className="sticky top-6 gap-20 flex flex-col">
              <PlanSummary
                currentSelectedCorePlatformPlan={
                  currentSelectedCorePlatformPlan
                }
                currentSelectedAutomationPlan={currentSelectedAutomationPlan}
                currentSelectedMarketingPlan={currentSelectedMarketingPlan}
                numUsersForCalculation={numUsersForCalculation}
                currentMonthlyTotal={currentMonthlyTotal}
                isDirty={isDirty}
                billingPreviewResponse={billingPreviewResponse}
                clickSaveChanges={clickSaveChanges}
                showResetSelectionsModal={showResetSelectionsModal}
                pendingRecalculation={pendingRecalculation}
                hasPendingDowngrade={hasAnyPendingPlans}
              />
            </div>
          </div>
        </div>
      </PageWrapper>
      <ConfirmPlanChangesDialog
        modalProps={confirmSaveChangesModalProps}
        billingPreviewResponse={billingPreviewResponse}
      />
      <DowngradeCancellationDialog {...cancelDowngradeModalProps} />
      <PendingPlanChangesDialog {...lockedPlanCategoryModalProps} />
      <UserCountErrorDialog {...invalidUserCountModalProps} />
      <UnsavedChangesDialog {...resetSelectionsModalProps} />
      <MissingBillingDetailsDialog {...missingBillingInfoModalProps} />
      <RequiresPaidPlanToCancelDowngrade {...cannotCancelDowngradeModalProps} />
      <ConfirmNavigationModal when={isDirty} />
    </PageSizing>
  );
};
